"use client";

import { useState, useCallback } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
  IconButton,
  Chip,
  Alert,
  Divider,
  Autocomplete,
} from "@mui/material";
import {
  NotificationsActive as NotifIcon,
  Close as CloseIcon,
  Send as SendIcon,
  AccessTime as ClockIcon,
  Inventory as MatIcon,
} from "@mui/icons-material";
import axios from "axios";
import moment from "moment";
import { UrlApi } from "../../../services/apirest";
import { calcularVencimientoPorPeriodo } from "../utils/calcvencimiento";
import { usePlanta } from "../context/PlantaContext";
import { useToast } from "../../../Hooks/UseToast";

const DIAS_ALERTA = 7;
const EMISOR_DEFAULT = localStorage.getItem("user") ?? "sistema";

// ─── NotificarEncargadoButton ──────────────────────────────────────────────────

export function NotificarEncargadoButton() {
  const { plantaSeleccionada, plantas } = usePlanta();
  const { showToast, Toast } = useToast();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [destinatario, setdestinatario] = useState(null);
  const [mensaje, setMensaje] = useState("");
  const [asunto, setAsunto] = useState("");
  const [alertas, setAlertas] = useState({ vencimientos: [], materiales: [] });
  const [usuarios, setUsuarios] = useState([]);

  const plantaNombre = plantas?.find((p) => p.id_planta === plantaSeleccionada)?.sucursal ?? "Planta";

  const fetchAlertas = useCallback(async () => {
    if (!plantaSeleccionada) return;
    setLoading(true);

    try {
      // Vencimientos próximos en los siguientes DIAS_ALERTA días
      const { data: vencData } = await axios.get(
        `${UrlApi}/mp_vencimientos/${plantaSeleccionada}`
      );

      const hoy = moment().startOf("day");
      const limite = moment().add(DIAS_ALERTA, "days").startOf("day");

      const vencimientosProximos = [];
      const alertasMateriales = [];

      (vencData ?? []).forEach((equipo) => {
        (equipo.tareas ?? []).forEach((tarea) => {
          const baseFecha = tarea.fecha_realizado || tarea.ultimo;
          const venc = calcularVencimientoPorPeriodo(baseFecha, tarea);

          if (venc.isSameOrBefore(limite)) {
            vencimientosProximos.push({
              equipo: equipo.nombre_equipo ?? equipo.equipo,
              tarea: tarea.tarea,
              vencimiento: venc.format("DD/MM/YYYY"),
              vencido: venc.isBefore(hoy),
            });
          }

          // Material alert (si existe y vence pronto)
          if (
            tarea.alerta_material &&
            moment(tarea.alerta_material).isSameOrBefore(limite)
          ) {
            alertasMateriales.push({
              equipo: equipo.nombre_equipo ?? equipo.equipo,
              tarea: tarea.tarea,
              materiales: tarea.materiales,
              alerta: moment(tarea.alerta_material).format("DD/MM/YYYY"),
              vencido: moment(tarea.alerta_material).isBefore(hoy),
            });
          }
        });
      });

      setAlertas({ vencimientos: vencimientosProximos, materiales: alertasMateriales });

      // Componer el mensaje automático
      const asuntoAuto = `Alertas de mantenimiento – ${plantaNombre} – ${moment().format("DD/MM/YYYY")}`;
      setAsunto(asuntoAuto);

      let body = "";

      if (vencimientosProximos.length > 0) {
        body += `TAREAS PRÓXIMAS A VENCER (${DIAS_ALERTA} días):\n`;
        vencimientosProximos.forEach((v) => {
          body += `• ${v.equipo} – ${v.tarea} – Vence: ${v.vencimiento}${v.vencido ? " ⚠ VENCIDO" : ""}\n`;
        });
      }

      if (alertasMateriales.length > 0) {
        if (body) body += "\n";
        body += `MATERIALES POR REPONER:\n`;
        alertasMateriales.forEach((m) => {
          body += `• ${m.equipo} – ${m.materiales} – Alerta: ${m.alerta}${m.vencido ? " ⚠ VENCIDO" : ""}\n`;
        });
      }

      if (!body) {
        body = "No hay alertas pendientes para los próximos 7 días.";
      }

      setMensaje(body);
    } catch (err) {
      console.error(err);
      showToast("Error al obtener datos de alertas", "error");
    } finally {
      setLoading(false);
    }
  }, [plantaSeleccionada, plantaNombre]);

  const handleOpen = () => {
    setOpen(true);
    setdestinatario(null);
    fetchAlertas();
    if (usuarios.length === 0) {
      axios.get(`${UrlApi}/userdata`).then(({ data }) => setUsuarios(data)).catch(() => {});
    }
  };

  const handleClose = () => {
    setOpen(false);
    setAlertas({ vencimientos: [], materiales: [] });
  };

  const handleSend = async () => {
    if (!destinatario) {
      showToast("Ingresá el usuario destinatario", "warning");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${UrlApi}/mensajes/`, {
        emisor: EMISOR_DEFAULT,
        destinatario: destinatario.username,
        referencia: asunto,
        mensaje,
      });
      showToast("Notificación enviada correctamente", "success");
      handleClose();
    } catch (err) {
      console.error(err);
      showToast("Error al enviar la notificación", "error");
    } finally {
      setSending(false);
    }
  };

  if (!plantaSeleccionada) return null;

  return (
    <>
      {Toast}
      <Button
        size="small"
        variant="outlined"
        startIcon={<NotifIcon sx={{ fontSize: 15 }} />}
        onClick={handleOpen}
        sx={{
          fontSize: 12,
          borderColor: "rgba(59,130,246,0.4)",
          color: "#1d4ed8",
          bgcolor: "rgba(59,130,246,0.05)",
          "&:hover": {
            bgcolor: "rgba(59,130,246,0.1)",
            borderColor: "#3b82f6",
          },
        }}
      >
        Notificar encargado
      </Button>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>
              Notificar al encargado
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {plantaNombre}
            </Typography>
          </Box>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {/* Resumen de alertas encontradas */}
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                <Chip
                  icon={<ClockIcon sx={{ fontSize: "14px !important" }} />}
                  label={`${alertas.vencimientos.length} vencimientos`}
                  size="small"
                  color={alertas.vencimientos.length > 0 ? "warning" : "default"}
                  variant="outlined"
                />
                <Chip
                  icon={<MatIcon sx={{ fontSize: "14px !important" }} />}
                  label={`${alertas.materiales.length} alertas de materiales`}
                  size="small"
                  color={alertas.materiales.length > 0 ? "warning" : "default"}
                  variant="outlined"
                />
              </Box>

              {alertas.vencimientos.length === 0 && alertas.materiales.length === 0 && (
                <Alert severity="success" sx={{ fontSize: 13 }}>
                  No hay alertas pendientes en los próximos {DIAS_ALERTA} días.
                </Alert>
              )}

              <Divider />

              {/* Destinatario */}
              <Autocomplete
                size="small"
                fullWidth
                options={usuarios}
                value={destinatario}
                onChange={(_, val) => setdestinatario(val)}
                getOptionLabel={(u) =>
                  u ? `${u.username} — ${u.nombrepila ?? ""} ${u.apellido ?? ""}`.trim() : ""
                }
                isOptionEqualToValue={(opt, val) => opt.username === val?.username}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Usuario destinatario"
                    required
                    helperText="Buscá por nombre de usuario, nombre o apellido"
                  />
                )}
              />

              {/* Asunto */}
              <TextField
                label="Asunto"
                size="small"
                fullWidth
                value={asunto}
                onChange={(e) => setAsunto(e.target.value)}
              />

              {/* Mensaje editable */}
              <TextField
                label="Mensaje"
                size="small"
                fullWidth
                multiline
                rows={8}
                value={mensaje}
                onChange={(e) => setMensaje(e.target.value)}
                InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }}
              />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5 }}>
          <Button onClick={handleClose} size="small">
            Cancelar
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={sending ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
            disabled={loading || sending || !mensaje}
            onClick={handleSend}
          >
            Enviar notificación
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
